import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '@/lib/api';
import type { User, AuthState } from '@/types';
import { logger } from '@/lib/logger';
import { secureStorage } from '@/lib/secureStorage';

interface AuthContextType extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const checkAuth = async () => {
    try {
      logger.auth('Checking authentication status');
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await api.getAuthStatus();
      logger.auth('Auth status response received');
      
      // Handle both direct response and wrapped response formats
      const authenticated = (response as any).authenticated || response.data?.authenticated;
      const user = (response as any).user || response.data?.user;
      
      if (authenticated && user) {
        logger.auth('User authenticated successfully');
        // Store user preferences securely
        secureStorage.setUserPreferences({ lastLogin: Date.now() });
        setAuthState({
          user: user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        logger.auth('User not authenticated');
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      logger.error('Authentication check failed', error);
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  };

  const login = () => {
    logger.auth('Initiating OAuth login');
    // Clear any existing secure storage before login
    secureStorage.clear();
    
    // Get API base URL dynamically (V5 single source)
    const getApiBaseUrl = (): string => {
      // V5: Use Vite environment variable - must be available in production
      return (import.meta as any).env.VITE_API_BASE_URL;
    };
    
    // Redirect to Google OAuth on backend
    window.location.href = `${getApiBaseUrl()}/google/login`;
  };

  const logout = async () => {
    try {
      logger.auth('Initiating logout');
      
      // Clear all browser storage thoroughly
      secureStorage.clear();
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear all cookies by setting them to expire
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      
      await api.logout();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      
      logger.auth('Logout completed successfully');
      // Simple redirect to home page - user will need to manually re-authenticate
      // Google OAuth session will expire naturally or user can clear browser cookies
      window.location.href = '/';
    } catch (error) {
      logger.error('Logout failed, forcing client-side logout', error);
      // Force logout on client side even if server request fails
      secureStorage.clear();
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear all cookies in error case too
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      // Redirect to home page in error case too
      window.location.href = '/';
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook for checking if user has admin role
export const useIsAdmin = (): boolean => {
  const { user } = useAuth();
  return user?.role === 'admin';
};

// Hook for requiring authentication
export const useRequireAuth = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isAuthenticated, isLoading, login]);
  
  return { isAuthenticated, isLoading };
};

// Hook for requiring admin access
export const useRequireAdmin = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  return {
    isAuthenticated,
    isAdmin,
    isLoading,
    hasAccess: isAuthenticated && isAdmin,
  };
};
